import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { MinimalRouterLink } from 'app/common/components/router-link';
import { Dropdown } from 'app/common/daisy/dropdown';
import ApiIcon from 'app/common/icons/api';
import EditorIcon from 'app/common/icons/editor';
import SettingsIcon from 'app/common/icons/settings';
import type React from 'react';
import { useMemo } from 'react';
import SettingsNav from '../../../common/components/nav/settings-nav';
import type { ProjectToolbarHookProps } from './project-toolbar-hook';

const TOOLBAR_ROUTES = {
  agents: ['/agents'],
  api: ['/api', '/schema', '/shape', '/query', '/mutation', '/services'],
  data: ['/assets', '/content', '/data'],
  settings: ['/settings', '/sites', '/workflows', '/roles', '/members', '/billing', '/api-keys', '/webhooks']
};

const shouldShowTab = (
  requiresProjectUpdate: boolean,
  requiresAgentView: boolean,
  canUpdateProject: boolean,
  canViewAgentSessions: boolean
): boolean => {
  if (requiresProjectUpdate && !canUpdateProject) return false;
  if (requiresAgentView && !canViewAgentSessions) return false;
  return true;
};

export type ProjectToolbarProps = ProjectToolbarHookProps;

function getProjectLocation(route: string) {
  const routeParts = route.split('/');
  const branchIndex = routeParts.indexOf(':branch');
  if (branchIndex >= 0) {
    return `/${routeParts[branchIndex + 1]}`;
  }

  const projectIdIndex = routeParts.indexOf(':projectId');
  return `/${routeParts[projectIdIndex + 1]}`;
}

function getActiveToolbarValue(route: string): string | boolean {
  const location = getProjectLocation(route);
  let activeItem: string | boolean = false;
  // Data currently lives at the project root, so we'll check for that first
  if (route === '/project/:projectId/branch/:branch') return 'home';
  // Otherwise, we'll try to find the first key in TOOLBAR_ROUTES that matches our route
  Object.entries(TOOLBAR_ROUTES).some(([value, paths]) => {
    if (paths.some((path) => location.endsWith(path))) {
      activeItem = value;
      return true;
    }

    return false;
  });
  return activeItem;
}

export const ProjectToolbar: React.FC<ProjectToolbarProps> = (props) => {
  const { projectId, branchPath, route, canUpdateProject, canViewAgentSessions } = props;
  const activeItem = useMemo(() => getActiveToolbarValue(route), [route]);

  if (!projectId) return null;

  return (
    <div className="tabs tabs-sm items-center flex-nowrap" role="tablist" aria-label="primary navigation">
      {shouldShowTab(false, true, canUpdateProject, canViewAgentSessions) && (
        <MinimalRouterLink
          key="agents"
          href={`${branchPath}/agents`}
          className={`tab tab-bordered flex items-center gap-2 px-4 flex-nowrap  ${activeItem === 'agents' ? 'tab-active' : ''}`}
        >
          <SmartToyIcon fontSize="small" />
          <span className="hidden lg:inline" data-testid="project-toolbar-agents-tab">
            Agents
          </span>
        </MinimalRouterLink>
      )}
      <MinimalRouterLink
        key="data"
        href={`${branchPath}/data`}
        className={`tab tab-bordered flex items-center gap-2 px-4 flex-nowrap  ${activeItem === 'data' || activeItem === 'home' ? 'tab-active' : ''}`}
      >
        <EditorIcon fontSize="small" />
        <span className="hidden lg:inline" data-testid="project-toolbar-data-tab">
          Data
        </span>
      </MinimalRouterLink>
      {shouldShowTab(true, false, canUpdateProject, canViewAgentSessions) && (
        <MinimalRouterLink
          key="api"
          href={`${branchPath}/api`}
          className={`tab tab-bordered flex items-center gap-2 px-4 flex-nowrap  ${activeItem === 'api' ? 'tab-active' : ''}`}
        >
          <ApiIcon fontSize="small" />
          <span className="hidden lg:inline" data-testid="project-toolbar-api-tab">
            API
          </span>
        </MinimalRouterLink>
      )}
      <Dropdown
        trigger={
          <div
            className={`tab tab-bordered flex items-center gap-2 flex-nowrap ${activeItem === 'settings' ? 'tab-active' : ''}`}
            data-testid="project-toolbar-settings-tab"
          >
            <SettingsIcon fontSize="small" />
            <span className="hidden lg:inline">Settings</span>
            <KeyboardArrowDownIcon className="hidden lg:inline" />
          </div>
        }
        dropdownClassName="bg-base-100 rounded-box shadow-sm w-48"
      >
        <SettingsNav />
      </Dropdown>
    </div>
  );
};

export default ProjectToolbar;
